export type CriticalNonconformityRow = {
  questionId: string;
  questionTitle: string;
  answerTitle: string;
  description: string;
  nodeTitle: string;
  sortOrder: number;
  documentId?: number | string | null;
  imageUrl?: string | null;
  imageName?: string | null;
  imageType?: string | null;
  sourceAnswerSnapshot?: any;
};

type BuildCriticalNonconformityRowsInput = {
  answers?: Record<string, any> | null;
  itemsByNode?: Record<string, Array<{ question?: any; answers?: any[] }>> | null;
  nodes?: Array<{ id?: number | string | null; title?: string | null; sortOrder?: number | null }> | null;
};

export type CriticalNonconformityImage = {
  documentId?: number | string | null;
  imageUrl?: string | null;
  imageName?: string | null;
  imageType?: string | null;
};

const textCollator = new Intl.Collator('tr', { sensitivity: 'base', numeric: true });

const ensureStringValue = (value: any): string => {
  if (value === null || value === undefined) {
    return '';
  }
  return typeof value === 'string' ? value : String(value);
};

const toOptionalBoolean = (value: any): boolean | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    if (value === 1) {
      return true;
    }
    if (value === 0) {
      return false;
    }
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1') {
      return true;
    }
    if (normalized === 'false' || normalized === '0') {
      return false;
    }
  }
  return undefined;
};

const normalizeNodeKey = (value: any): string | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  return String(value);
};

const normalizeSortOrder = (value: any, fallback: number): number => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const getQuestionKey = (question: any): string | undefined => {
  if (question?.id === null || question?.id === undefined || question?.id === '') {
    return undefined;
  }
  return String(question.id);
};

const normalizeSelectedAnswers = (value: any): any[] => {
  if (value === null || value === undefined || value === '') {
    return [];
  }
  return Array.isArray(value) ? value : [value];
};

const getSelectedAnswerId = (value: any): string | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  if (typeof value === 'object') {
    if (value.id === null || value.id === undefined || value.id === '') {
      return undefined;
    }
    return String(value.id);
  }
  return String(value);
};

const findAnswerOption = (answerOptions: any[], selectedAnswer: any) => {
  const selectedId = getSelectedAnswerId(selectedAnswer);
  if (!selectedId) {
    return undefined;
  }
  return answerOptions.find((option) => String(option?.id) === selectedId);
};

const resolveAnswerTitle = (selectedAnswer: any, answerOption: any): string => {
  const fromSaved =
    typeof selectedAnswer === 'object'
      ? ensureStringValue(selectedAnswer?.value ?? selectedAnswer?.title).trim()
      : '';
  const fromOption = ensureStringValue(answerOption?.title ?? answerOption?.value).trim();
  return fromSaved || fromOption || '-';
};

const resolveAnswerDescription = (selectedAnswer: any): string => {
  if (!selectedAnswer || typeof selectedAnswer !== 'object') {
    return '-';
  }
  const description = ensureStringValue(selectedAnswer?.description).trim();
  return description || '-';
};

const pickFirstDefined = (source: Record<string, any>, keys: string[]) => {
  for (const key of keys) {
    if (source[key] !== undefined && source[key] !== null && source[key] !== '') {
      return source[key];
    }
  }
  return undefined;
};

const normalizeImagePayload = (value: any): CriticalNonconformityImage | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }

  const raw = value as Record<string, any>;
  const documentId = pickFirstDefined(raw, ['documentId', 'document_id', 'docId', 'docID']);
  const imageUrl = pickFirstDefined(raw, ['imageUrl', 'url', 'preSignUrl', 'preSignedUrl']);
  const imageName = pickFirstDefined(raw, ['imageName', 'name', 'fileName']);
  const imageType = pickFirstDefined(raw, ['imageType', 'mimeType', 'contentType']);

  if (documentId === undefined && imageUrl === undefined) {
    return undefined;
  }

  return {
    documentId: documentId ?? null,
    imageUrl: imageUrl === undefined ? null : String(imageUrl),
    imageName: imageName === undefined ? null : String(imageName),
    imageType: imageType === undefined ? null : String(imageType),
  };
};

export const extractCriticalNonconformityImage = (snapshot: any): CriticalNonconformityImage | undefined => {
  if (Array.isArray(snapshot)) {
    const falseAnswerImage = snapshot
      .filter((item) => toOptionalBoolean(item?.isTrue) === false)
      .map(extractCriticalNonconformityImage)
      .find(Boolean);
    if (falseAnswerImage) {
      return falseAnswerImage;
    }
    return snapshot.map(extractCriticalNonconformityImage).find(Boolean);
  }

  const directImage = normalizeImagePayload(snapshot);
  if (directImage) {
    return directImage;
  }

  if (!snapshot || typeof snapshot !== 'object') {
    return undefined;
  }

  for (const nested of Object.values(snapshot)) {
    const nestedImage = extractCriticalNonconformityImage(nested);
    if (nestedImage) {
      return nestedImage;
    }
  }
  return undefined;
};

export const buildCriticalNonconformityRows = ({
  answers,
  itemsByNode,
  nodes,
}: BuildCriticalNonconformityRowsInput): CriticalNonconformityRow[] => {
  if (!answers || typeof answers !== 'object' || !itemsByNode || typeof itemsByNode !== 'object') {
    return [];
  }

  const nodeTitleByKey = new Map<string, string>();
  const nodeOrderByKey = new Map<string, number>();
  (nodes || []).forEach((node, index) => {
    const key = normalizeNodeKey(node?.id);
    if (!key) {
      return;
    }
    nodeTitleByKey.set(key, ensureStringValue(node?.title).trim() || '-');
    nodeOrderByKey.set(key, normalizeSortOrder(node?.sortOrder, index + 1));
  });

  const rows: CriticalNonconformityRow[] = [];

  Object.entries(itemsByNode).forEach(([nodeKey, bundles]) => {
    if (!Array.isArray(bundles)) {
      return;
    }
    const nodeTitle = nodeTitleByKey.get(nodeKey) || '-';
    const nodeOrder = nodeOrderByKey.get(nodeKey) ?? Number.MAX_SAFE_INTEGER;

    bundles.forEach((bundle, index) => {
      const question = bundle?.question;
      const questionKey = getQuestionKey(question);
      if (
        !questionKey
        || question?.questionType !== 'MULTIPLE'
      ) {
        return;
      }

      const selectedAnswers = normalizeSelectedAnswers(answers[questionKey]);
      if (!selectedAnswers.length) {
        return;
      }

      const answerOptions = Array.isArray(bundle?.answers) ? bundle.answers : [];
      selectedAnswers.forEach((selectedAnswer) => {
        const answerOption = findAnswerOption(answerOptions, selectedAnswer);
        const selectedIsTrue =
          typeof selectedAnswer === 'object'
            ? toOptionalBoolean(selectedAnswer?.isTrue) ?? toOptionalBoolean(answerOption?.isTrue)
            : toOptionalBoolean(answerOption?.isTrue);

        if (selectedIsTrue !== false) {
          return;
        }

        const questionOrder = normalizeSortOrder(question?.sortOrder, index + 1);
        const image = extractCriticalNonconformityImage(selectedAnswer);
        rows.push({
          questionId: questionKey,
          questionTitle: ensureStringValue(question?.title).trim() || `Soru #${questionKey}`,
          answerTitle: resolveAnswerTitle(selectedAnswer, answerOption),
          description: resolveAnswerDescription(selectedAnswer),
          nodeTitle,
          sortOrder: nodeOrder * 100000 + questionOrder,
          documentId: image?.documentId,
          imageUrl: image?.imageUrl,
          imageName: image?.imageName,
          imageType: image?.imageType,
          sourceAnswerSnapshot: selectedAnswer,
        });
      });
    });
  });

  return rows.sort((left, right) => {
    if (left.sortOrder !== right.sortOrder) {
      return left.sortOrder - right.sortOrder;
    }
    return textCollator.compare(left.questionTitle, right.questionTitle);
  });
};
